import { useState } from 'react';

interface BusinessModelFormProps {
  channelList?: Api.Application.ChannelResponse[];
  loadingChannels?: boolean;
  selectedChannels?: string[];
  onChannelChange?: (channels: string[]) => void;
  releaseNote?: string;
  onReleaseNoteChange?: (note: string) => void;
}

export default function BusinessModelForm({
  channelList = [],
  loadingChannels = false,
  selectedChannels,
  onChannelChange,
  releaseNote = '',
  onReleaseNoteChange,
}: BusinessModelFormProps) {
  const [internalChannels, setInternalChannels] = useState<string[]>([]);

  const currentChannels = selectedChannels !== undefined ? selectedChannels : internalChannels;

  const toggle = (chId: string) => {
    const next = currentChannels.includes(chId)
      ? currentChannels.filter((c) => c !== chId)
      : [...currentChannels, chId];
    if (onChannelChange) {
      onChannelChange(next);
    } else {
      setInternalChannels(next);
    }
  };


  return (
    <div className="space-y-6">
      <p className="text-sm text-foreground-600 mb-4">选择目标发行渠道。不同渠道的合作条款和收益模式略有差异，提交后将由平台逐一确认。</p>

      {/* 目标发行渠道 */}
      <div>
        <label className="block text-sm font-bold text-foreground-800 mb-3">目标发行渠道（可多选）</label>
        {loadingChannels ? (
          <div className="py-8 text-center text-sm text-foreground-400">正在加载发行渠道...</div>
        ) : channelList.length === 0 ? (
          <div className="py-8 text-center text-sm text-foreground-400">暂无可选发行渠道</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {channelList.map((ch) => {
              const chId = ch.id || ch.channel_code || '';
              const selected = currentChannels.includes(chId);
              return (
                <button
                  key={chId}
                  onClick={() => toggle(chId)}
                  className={`relative text-left p-4 sm:p-5 rounded-xl border-2 transition-all cursor-pointer ${
                    selected ? 'border-primary-500 bg-primary-50/40 shadow-2xs' : 'border-gray-200/80 hover:border-gray-300'
                  }`}
                  type="button"
                >
                  {ch.enabled && (
                    <span className={`absolute top-3.5 right-3.5 text-xs px-2.5 py-0.5 rounded-full font-medium ${
                      selected ? 'bg-primary-500 text-foreground-950 font-bold' : 'bg-gray-100 text-foreground-500'
                    }`}>推荐</span>
                  )}
                  <div className="flex items-center gap-3.5 mb-2.5">
                    <div className={`w-10 h-10 flex items-center justify-center rounded-lg ${selected ? 'bg-primary-500 text-foreground-950' : 'bg-gray-100 text-foreground-500'}`}>
                      {ch.icon_asset?.access_url ? (
                        <img src={ch.icon_asset.access_url} alt={ch.channel_name} className="w-6 h-6 object-contain" />
                      ) : (
                        <i className="ri-tv-line text-xl"></i>
                      )}
                    </div>
                    <span className={`text-base font-bold ${selected ? 'text-primary-800' : 'text-foreground-900'}`}>{ch.channel_name}</span>
                  </div>
                  <p className="text-xs md:text-sm text-foreground-500 leading-relaxed">{ch.channel_description || ch.settlement_cycle_note || '基础发行分发渠道'}</p>
                </button>
              );
            })}
          </div>
        )}
      </div>


      {/* 发行要求 / 选填备注 */}
      <div>
        <label className="block text-sm font-bold text-foreground-800 mb-2">
          特殊发行要求 / 备注说明
        </label>
        <textarea
          value={releaseNote}
          onChange={(e) => onReleaseNoteChange?.(e.target.value)}
          placeholder="可填写排他上线时间、目标国家/地区或特殊发号期望等说明..."
          rows={3}
          className="w-full px-4 py-3 rounded-lg bg-white border border-gray-200/80 text-base text-foreground-950 placeholder-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-400/20 transition-all resize-none"
        />
      </div>

      {/* Note */}
      <div className="p-4.5 sm:p-5 rounded-xl border border-primary-200/60 bg-primary-50 flex items-start gap-3.5">
        <i className="ri-information-line text-primary-500 text-lg mt-0.5"></i>
        <p className="text-sm text-foreground-700 leading-relaxed">最终合作条款以线下签约确认为准，本页面仅为意向收集。提交后平台运营将在 2 个工作日内联系您。</p>
      </div>
    </div>
  );
}